import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Truck, User, CheckCircle2, Camera, Download, Printer, X, FileText, ChevronUp, ChevronDown, AlertTriangle, Scissors, RefreshCw, CircleDashed, Clock } from "lucide-react";
import API from "../../services/api";
import EditableCell from "./EditableCell";
import { useCanEdit, ReadOnlyBanner } from "../../services/permissions";
import {
  downloadWithAuth, deliveryOf, prettyDate, fmtTime, exceptionsOf, ExceptionBadges,
  RUN_STATUSES, sortRunsForPicker, runLabel, parseChip, fmtProducts,
} from "./util";

// How often the open run is re-read while the truck is still out. The driver
// app writes POD as it happens; this is what makes it show up here without
// the operator bouncing in and out of the run.
const LIVE_REFRESH_MS = 20000;

function PodCell({ job }) {
  const pod = job.pod || null;
  const photos = Number(job.photo_count ?? pod?.photos ?? 0) || 0;
  if (job.delivered) {
    const tip = [
      pod?.completed_by && `by ${pod.completed_by}`,
      pod?.note,
      pod?.no_photo_reason && `no photo: ${pod.no_photo_reason}`,
      pod?.lat != null && pod?.lon != null && `${pod.lat}, ${pod.lon}`,
    ].filter(Boolean).join(" · ");
    return (
      <div className="flex items-center gap-1.5 whitespace-nowrap text-emerald-400" title={tip || "Delivered"}>
        <CheckCircle2 size={14} />
        <span className="tnum">{fmtTime(job.delivered_at || pod?.completed_at) || "done"}</span>
        <span className={`flex items-center gap-0.5 ${photos ? "text-slate-300" : "text-slate-600"}`} title={`${photos} photo${photos === 1 ? "" : "s"}`}>
          <Camera size={12} />{photos}
        </span>
      </div>
    );
  }
  if (job.picked_up) {
    return (
      <span className="text-sky-400 text-[10px] uppercase tracking-wide whitespace-nowrap">
        Picked up{job.picked_up_at ? ` ${fmtTime(job.picked_up_at)}` : ""}
      </span>
    );
  }
  return <span className="text-slate-600">—</span>;
}

export default function RunDetail({ runId, runs, onBack, onChanged, onOpenRun, reportError, notify }) {
  const canEdit = useCanEdit();
  const [run, setRun] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [splitName, setSplitName] = useState("");
  const [splitDriver, setSplitDriver] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [startDraft, setStartDraft] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);

  // The parent's callbacks are read through refs so a re-render upstream
  // can never re-arm the fetch effect below (that is how polling loops start).
  const reportRef = useRef(reportError);
  reportRef.current = reportError;
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;

  const load = useCallback(async (silent) => {
    try {
      const res = await API.get(`/runs/${runId}`);
      setRun(res.data);
      setStartDraft(res.data?.start_time || "");
      setRefreshedAt(new Date());
    } catch (err) {
      if (!silent) reportRef.current(err);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, t] = await Promise.all([API.get("/drivers"), API.get("/trucks")]);
        if (cancelled) return;
        setDrivers(Array.isArray(d.data) ? d.data : []);
        setTrucks(Array.isArray(t.data) ? t.data : []);
      } catch (err) {
        if (!cancelled) reportRef.current(err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live POD while the run is still out; a finished run is static.
  const runStatus = run?.status;
  useEffect(() => {
    if (!runStatus || runStatus === "completed" || runStatus === "cancelled") return undefined;
    const id = setInterval(() => { if (!document.hidden) load(true); }, LIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [runStatus, load]);

  const jobs = useMemo(() => run?.jobs || [], [run]);
  const otherRuns = useMemo(
    () => sortRunsForPicker((runs || []).filter((r) => String(r.id) !== String(runId))),
    [runs, runId]);

  const patchRun = async (body) => {
    setBusy(true);
    try {
      const res = await API.patch(`/runs/${runId}`, body);
      const data = res.data || {};
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      // an older API build answers {message, id} - fall back to a refetch
      if (Array.isArray(data.jobs)) {
        setRun(data);
        setStartDraft(data.start_time || "");
        setRefreshedAt(new Date());
      } else {
        await load();
      }
      changedRef.current && changedRef.current();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const saveStop = async (jobId, apiField, value) => {
    try {
      await API.patch(`/jobs/${encodeURIComponent(jobId)}/stop-data`, { [apiField]: value });
      await load();
    } catch (err) {
      reportError(err);
    }
  };

  const removeFromRun = async (jobId) => {
    try {
      await API.patch(`/jobs/${encodeURIComponent(jobId)}/status`, { run_id: null });
      setSelected((s) => { const n = new Set(s); n.delete(jobId); return n; });
      await load();
      changedRef.current && changedRef.current();
    } catch (err) {
      reportError(err);
    }
  };

  const deleteRun = async () => {
    if (!window.confirm("Delete this run? Its stops go back to the unassigned pool.")) return;
    try {
      await API.delete(`/runs/${runId}`);
      changedRef.current && changedRef.current();
      onBack();
    } catch (err) {
      reportError(err);
    }
  };

  const download = async (path, filename) => {
    try { await downloadWithAuth(path, filename); }
    catch (err) { reportError(err); }
  };

  // Swap with the neighbour, show it immediately, then persist the whole
  // order (the server renumbers 1..n from the list it is given).
  const move = async (index, delta) => {
    const j = index + delta;
    if (j < 0 || j >= jobs.length) return;
    const ordered = [...jobs];
    [ordered[index], ordered[j]] = [ordered[j], ordered[index]];
    setRun((r) => ({ ...r, jobs: ordered.map((x, k) => ({ ...x, run_sequence: k + 1 })) }));
    try {
      await API.post(`/runs/${runId}/reorder`, { job_ids: ordered.map((x) => x.id) });
    } catch (err) {
      reportError(err);
    } finally {
      load(true);
    }
  };

  const moveToRun = async (jobId, targetId) => {
    const target = otherRuns.find((r) => String(r.id) === String(targetId));
    try {
      await API.post(`/runs/${targetId}/assign-job/${encodeURIComponent(jobId)}`);
      setSelected((s) => { const n = new Set(s); n.delete(jobId); return n; });
      notify(`Stop moved to ${target ? runLabel(target) : `run #${targetId}`}`, target ? target.id : Number(targetId));
      await load();
      changedRef.current && changedRef.current();
    } catch (err) {
      reportError(err);
    }
  };

  const splitSelected = async () => {
    // keep drive order, not click order, so the new sheet reads top-down
    const jobIds = jobs.filter((j) => selected.has(j.id)).map((j) => j.id);
    if (!jobIds.length) return;
    setBusy(true);
    try {
      const body = { job_ids: jobIds };
      if (splitName.trim()) body.name = splitName.trim();
      if (splitDriver) body.driver_id = splitDriver;
      const res = await API.post(`/runs/${runId}/split`, body);
      const nr = res.data || {};
      setSelected(new Set());
      setSplitName("");
      setSplitDriver("");
      notify(`${jobIds.length} stop${jobIds.length === 1 ? "" : "s"} moved into ${nr.name || (nr.id ? `Run #${nr.id}` : "a new run")}`, nr.id || null);
      await load();
      changedRef.current && changedRef.current();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === jobs.length) setSelected(new Set());
    else setSelected(new Set(jobs.map((j) => j.id)));
  };

  if (!run) {
    return (
      <div className="p-6 max-w-[1700px] mx-auto">
        <button onClick={onBack} className="text-blue-400 hover:text-blue-300 flex items-center gap-2 mb-4">← Back to Runs</button>
        <div className="p-8 text-center text-slate-400">Loading run…</div>
      </div>
    );
  }

  const delivery = deliveryOf(run);
  const progress = run.progress && typeof run.progress === "object"
    ? { delivered: Number(run.progress.delivered || 0), total: Number(run.progress.total ?? jobs.length) }
    : { delivered: jobs.filter((j) => j.delivered).length, total: jobs.length };
  const exceptionCount = typeof run.exception_count === "number"
    ? run.exception_count
    : jobs.filter((j) => exceptionsOf(j).length).length;
  const statusOptions = RUN_STATUSES.includes(run.status) || !run.status ? RUN_STATUSES : [run.status, ...RUN_STATUSES];
  const driverKnown = !run.driver_id || drivers.some((d) => String(d.id) === String(run.driver_id));
  const truckKnown = !run.truck_id || trucks.some((t) => String(t.id) === String(run.truck_id));
  const live = run.status !== "completed" && run.status !== "cancelled";

  // Dispatch-sheet columns (operator requested): every cell except # /
  // Invoice / Parse / POD is click-to-edit and persists via /jobs/{id}/stop-data.
  // The leading select column only exists to feed Split, so it goes away
  // entirely for a reader rather than leaving a dead checkbox in every row.
  const cols = [...(canEdit ? [""] : []), "#", "Invoice/PO", "S#", "Customer", "Address Line", "Warehouse",
                "Phone", "Pickup From", "Store", "Products", "Instructions", "Flags", "Parse", "POD", ""];

  return (
    <div className="p-6 max-w-[1700px] mx-auto" id="run-print-area">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2 print:hidden">
        <button onClick={onBack} className="text-blue-400 hover:text-blue-300 flex items-center gap-2">
          ← Back to Runs
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Deleting a run throws its stops back in the pool - hidden outright
              rather than shown dead, so nobody reaches for it. The three
              exports below stay: taking the sheet to the truck is reading. */}
          {canEdit && (
            <button onClick={deleteRun}
              className="bg-slate-800 hover:bg-red-900/40 text-red-400 border border-red-500/30 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5">
              <X size={15} /> Delete Run
            </button>
          )}
          <button onClick={() => download(`/runs/${run.id}/source.pdf`, `run-${run.id}-source-docs.pdf`)}
            title="Every stop's source documents merged into one PDF for verification"
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5">
            <FileText size={15} /> Source Docs
          </button>
          <button onClick={() => download(`/runs/${run.id}/export.xlsx`, `run-${run.id}.xlsx`)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5">
            <Download size={15} /> Excel
          </button>
          <button onClick={() => download(`/runs/${run.id}/sheet.pdf`, `run-${run.id}-sheet.pdf`)}
            title="Print-ready A4 run sheet with a signature column"
            className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5">
            <Printer size={15} /> Print sheet
          </button>
        </div>
      </div>

      {!canEdit && <ReadOnlyBanner className="mb-4" />}

      <div className="metric-card bg-slate-800/50 rounded-xl p-5 border border-slate-700 print:bg-white print:border-0 print:animate-none">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-5">
          <div className="min-w-0">
            {!canEdit ? (
              <h2 className="text-2xl font-bold text-white mb-1 print:text-black">
                {run.name || `Run #${run.id}`}
              </h2>
            ) : editingName ? (
              <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                onBlur={() => { setEditingName(false); if (nameDraft.trim() !== (run.name || "")) patchRun({ name: nameDraft.trim() }); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.target.blur();
                  else if (e.key === "Escape") { setNameDraft(run.name || ""); setEditingName(false); }
                }}
                className="text-2xl font-bold bg-slate-950 border border-blue-500 rounded px-2 py-0.5 text-white outline-none mb-1 w-full max-w-md" />
            ) : (
              <h2 onClick={() => { setNameDraft(run.name || ""); setEditingName(true); }} title="Click to rename"
                className="text-2xl font-bold text-white mb-1 print:text-black cursor-pointer hover:text-blue-300">
                {run.name || `Run #${run.id}`}
              </h2>
            )}
            {/* Who drives, which truck, what day, what time: every one of these
                is a PATCH the moment it changes, so read-only gets the same
                facts as flat text instead of live-looking controls. */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-400 text-sm print:text-gray-600">
              <label className="flex items-center gap-1.5" title="The day this run is driven">
                <Truck size={15} className="text-emerald-500/80" />
                <span>Deliver</span>
                {canEdit ? (
                  <input type="date" value={delivery} disabled={busy}
                    onChange={(e) => { const v = e.target.value; if (/^\d{4}-\d{2}-\d{2}$/.test(v) && v !== delivery) patchRun({ delivery_date: v }); }}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-200 text-xs outline-none focus:border-blue-500 [&::-webkit-calendar-picker-indicator]:invert" />
                ) : (
                  <span className="text-slate-200 text-xs font-medium">{delivery || "—"}</span>
                )}
                <span className="text-[11px] text-slate-500">{prettyDate(delivery)} · received {run.date}</span>
              </label>
              <label className="flex items-center gap-1.5" title="Depot departure time">
                <Clock size={15} className="text-slate-500" />
                <span>Start</span>
                {canEdit ? (
                  <input type="time" value={startDraft} disabled={busy}
                    onChange={(e) => setStartDraft(e.target.value)}
                    onBlur={() => { if ((startDraft || "") !== (run.start_time || "")) patchRun({ start_time: startDraft || null }); }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-200 text-xs outline-none focus:border-blue-500 [&::-webkit-calendar-picker-indicator]:invert" />
                ) : (
                  <span className="text-slate-200 text-xs font-medium">{run.start_time || "—"}</span>
                )}
              </label>
              <label className="flex items-center gap-1.5">
                <User size={15} className="text-slate-500" />
                <span>Driver</span>
                {canEdit ? (
                  <select value={run.driver_id || ""} disabled={busy}
                    onChange={(e) => patchRun({ driver_id: e.target.value || null })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-200 text-xs outline-none focus:border-blue-500 max-w-[180px]">
                    <option value="">Unassigned</option>
                    {!driverKnown && <option value={run.driver_id}>{run.driver_name || run.driver_id}</option>}
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>)}
                  </select>
                ) : (
                  <span className="text-slate-200 text-xs font-medium">
                    {run.driver_name || (run.driver_id ? `Driver ${run.driver_id}` : "Unassigned")}
                  </span>
                )}
              </label>
              <label className="flex items-center gap-1.5">
                <Truck size={15} className="text-slate-500" />
                <span>Truck</span>
                {canEdit ? (
                  <select value={run.truck_id || ""} disabled={busy}
                    onChange={(e) => patchRun({ truck_id: e.target.value || null })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-200 text-xs outline-none focus:border-blue-500 max-w-[160px]">
                    <option value="">No truck</option>
                    {!truckKnown && <option value={run.truck_id}>{run.truck_plate || run.truck_id}</option>}
                    {trucks.map((t) => <option key={t.id} value={t.id}>{t.license_plate || t.id}</option>)}
                  </select>
                ) : (
                  <span className="text-slate-200 text-xs font-medium">
                    {run.truck_plate || (run.truck_id ? `Truck ${run.truck_id}` : "No truck")}
                  </span>
                )}
              </label>
              <span>{jobs.length} stop{jobs.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap print:hidden">
            <span className={`px-3 py-1 rounded-full border text-xs font-medium tnum ${progress.total && progress.delivered === progress.total ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-slate-900 text-slate-300 border-slate-700"}`}
              title="Stops delivered (proof of delivery from the driver app)">
              {progress.delivered}/{progress.total} delivered
            </span>
            {exceptionCount > 0 && (
              <span className="px-3 py-1 rounded-full border text-xs font-medium bg-amber-500/10 text-amber-300 border-amber-500/30 flex items-center gap-1"
                title="Stops missing an address, phone, items, or still needing info">
                <AlertTriangle size={12} /> {exceptionCount} exception{exceptionCount === 1 ? "" : "s"}
              </span>
            )}
            {canEdit ? (
              <select value={run.status || "draft"} disabled={busy}
                onChange={(e) => patchRun({ status: e.target.value })}
                className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full border border-blue-500/20 capitalize font-medium text-xs outline-none">
                {statusOptions.map((s) => <option key={s} value={s} className="bg-slate-900 text-slate-200">{s.replace(/_/g, " ")}</option>)}
              </select>
            ) : (
              <span className="bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full border border-blue-500/20 capitalize font-medium text-xs">
                {String(run.status || "draft").replace(/_/g, " ")}
              </span>
            )}
            <button onClick={() => load()} title={refreshedAt ? `Last refreshed ${refreshedAt.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}${live ? " · live" : ""}` : "Refresh"}
              className="text-slate-500 hover:text-white p-1">
              {busy ? <CircleDashed size={15} className="animate-spin" /> : <RefreshCw size={15} className={live ? "text-emerald-500/70" : ""} />}
            </button>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200 flex items-start gap-2 print:hidden">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div className="flex-1">{warnings.map((w, i) => <div key={i}>{String(w)}</div>)}</div>
            <button onClick={() => setWarnings([])} className="text-amber-300/70 hover:text-white"><X size={14} /></button>
          </div>
        )}

        {canEdit && selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2 text-xs print:hidden">
            <Scissors size={14} className="text-indigo-300" />
            <span className="text-indigo-200 font-semibold">{selected.size} selected</span>
            <input value={splitName} onChange={(e) => setSplitName(e.target.value)} placeholder="New run name (optional)"
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200 outline-none focus:border-indigo-500 w-52" />
            <select value={splitDriver} onChange={(e) => setSplitDriver(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200 outline-none focus:border-indigo-500">
              <option value="">Driver (optional)</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>)}
            </select>
            <button onClick={splitSelected} disabled={busy}
              className="bg-indigo-600 hover:bg-indigo-500 text-white rounded px-3 py-1 font-medium disabled:opacity-50">
              {busy ? "Splitting…" : "Split selected into new run"}
            </button>
            <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-white px-2 py-1">Clear</button>
          </div>
        )}

        <p className="text-[11px] text-slate-500 mb-2 print:hidden">
          {canEdit
            ? "Click any cell to correct it — changes save to the job immediately. Use the arrows to set drive order."
            : "This run sheet is view only. Exports and source documents above still work."}
        </p>
        <div className="overflow-x-auto rounded-lg border border-slate-700/60">
          <table className="w-full text-xs text-slate-300 print:text-black">
            <thead className="bg-slate-950/60 text-slate-400 text-[10px] uppercase tracking-wide print:bg-gray-100 print:text-black">
              <tr>
                {cols.map((c, ci) => (
                  <th key={ci} className="px-2.5 py-2 text-left whitespace-nowrap">
                    {canEdit && ci === 0 ? (
                      <input type="checkbox" checked={jobs.length > 0 && selected.size === jobs.length} onChange={toggleAll}
                        title="Select all for split" className="accent-indigo-500 print:hidden" />
                    ) : c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {jobs.map((job, i) => {
                const save = (apiField) => (_f, _old, val) => saveStop(job.id, apiField, val);
                const rowTint = job.parse_status === "ok" ? "row-ok"
                  : job.parse_status === "low_confidence" ? "row-low"
                  : job.parse_status === "unidentified" ? "row-unident" : "";
                const codes = exceptionsOf(job);
                return (
                <tr key={job.id} className={`hover:bg-slate-900/40 align-top ${rowTint} ${job.delivered ? "bg-emerald-500/[0.04]" : ""} ${selected.has(job.id) ? "bg-indigo-500/10" : ""} print:shadow-none`}>
                  {canEdit && (
                    <td className="px-2.5 py-2 print:hidden">
                      <input type="checkbox" checked={selected.has(job.id)}
                        onChange={() => setSelected((s) => { const n = new Set(s); if (n.has(job.id)) n.delete(job.id); else n.add(job.id); return n; })}
                        className="accent-indigo-500" />
                    </td>
                  )}
                  <td className="px-2.5 py-2 text-slate-500">
                    <div className="flex items-center gap-1">
                      <span className="tnum w-5">{job.run_sequence || i + 1}</span>
                      {/* Reordering is a POST /reorder - the drive order the
                          truck actually follows - so the arrows go, not grey out. */}
                      {canEdit && (
                        <div className="flex flex-col print:hidden">
                          <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                            className="text-slate-600 hover:text-white disabled:opacity-20 leading-none"><ChevronUp size={12} /></button>
                          <button onClick={() => move(i, 1)} disabled={i === jobs.length - 1} title="Move down"
                            className="text-slate-600 hover:text-white disabled:opacity-20 leading-none"><ChevronDown size={12} /></button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2.5 py-2 font-mono whitespace-nowrap">{job.invoiceNo || job.order_number || "—"}</td>
                  <EditableCell value={job.so_number || ""} field="so" onSave={save("order_or_so_number")} readOnly={!canEdit}
                    className="px-2.5 py-2 font-mono text-indigo-400 whitespace-nowrap" />
                  <EditableCell value={job.customer_name || ""} field="cust" onSave={save("customer_name")} readOnly={!canEdit}
                    className="px-2.5 py-2 whitespace-nowrap font-medium text-white" />
                  <EditableCell value={job.address_line || job.destination_address || ""} field="addr" onSave={save("address_line")} readOnly={!canEdit}
                    className="px-2.5 py-2 min-w-[180px]" />
                  <EditableCell value={job.warehouse_name || ""} field="wh" onSave={save("warehouse_name")} readOnly={!canEdit}
                    className="px-2.5 py-2 whitespace-nowrap" />
                  <EditableCell value={job.recipient_phone || ""} field="ph" onSave={save("recipient_phone_number")} readOnly={!canEdit}
                    className="px-2.5 py-2 font-mono whitespace-nowrap" />
                  <EditableCell value={job.pickup_from || job.comingFrom || ""} field="pickup" onSave={save("pickup_from")} readOnly={!canEdit}
                    className="px-2.5 py-2 whitespace-nowrap" />
                  <EditableCell value={job.store_name || ""} field="store" onSave={save("store")} readOnly={!canEdit}
                    className="px-2.5 py-2 whitespace-nowrap" />
                  <td className="px-2.5 py-2 max-w-[200px] truncate" title={fmtProducts(job.products)}>{fmtProducts(job.products)}</td>
                  <EditableCell value={job.run_instructions || ""} field="instr" onSave={save("instructions")} readOnly={!canEdit}
                    className="px-2.5 py-2 min-w-[160px] text-slate-400" />
                  <td className="px-2.5 py-2"><ExceptionBadges codes={codes} /></td>
                  <td className="px-2.5 py-2">{parseChip(job.parse_status)}</td>
                  <td className="px-2.5 py-2"><PodCell job={job} /></td>
                  <td className="px-2.5 py-2 print:hidden whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => download(`/jobs/${encodeURIComponent(job.id)}/source.pdf`, `${job.invoiceNo || job.order_number || job.id}-source.pdf`)}
                        title="Download this stop's source documents"
                        className="text-slate-500 hover:text-indigo-400"><FileText size={14} /></button>
                      {canEdit && (
                        <select value="" onChange={(e) => e.target.value && moveToRun(job.id, e.target.value)}
                          title="Move this stop to another run"
                          className="bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-[10px] text-slate-400 max-w-[110px] outline-none">
                          <option value="">Move to run…</option>
                          {otherRuns.map((r) => <option key={r.id} value={String(r.id)}>{runLabel(r)}</option>)}
                        </select>
                      )}
                      {canEdit && (
                        <button onClick={() => removeFromRun(job.id)} title="Remove from run"
                          className="text-slate-600 hover:text-red-400"><X size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr><td colSpan={cols.length} className="text-center p-8 text-slate-500">
                  No stops yet — go back and assign customer deliveries to this run.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
